import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { token: authToken } = useAuth();
  const [status, setStatus] = useState('loading');
  const sessionId = searchParams.get('session_id');   // Stripe
  const paypalOrderId = searchParams.get('token');     // PayPal
  const gateway = searchParams.get('gateway');
  const paymentType = searchParams.get('type');
  const isPurchase = paymentType === 'purchase';
  const isCourseEnrollment = paymentType === 'course_enrollment';
  const isEventRegistration = paymentType === 'event_registration';

  const started = useRef(false);

  useEffect(() => {
    // Guard against a double run (StrictMode / re-render) so a PayPal order is only captured once.
    if (started.current) return;
    started.current = true;
    if (gateway === 'paypal' && paypalOrderId) {
      capturePaypal();
    } else if (sessionId) {
      pollStripeStatus();
    } else {
      setStatus('error');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const capturePaypal = async () => {
    try {
      await axios.post(
        `${API_URL}/api/payments/paypal/capture-order`,
        { order_id: paypalOrderId },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const pollStripeStatus = async () => {
    const maxAttempts = 5;
    let current = 0;
    const poll = async () => {
      if (current >= maxAttempts) { setStatus('timeout'); return; }
      try {
        const res = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') { setStatus('success'); return; }
        if (res.data.status === 'expired') { setStatus('expired'); return; }
        current++;
        setTimeout(poll, 2000);
      } catch { setStatus('error'); }
    };
    poll();
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-4" data-testid="payment-success-page">
      <Card className="max-w-md w-full shadow-xl">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-[#003366] mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold mb-2">Processing Payment</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-emerald-700">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                {isPurchase
                  ? 'Your purchase is confirmed and has been added to your account.'
                  : isCourseEnrollment
                  ? "You're enrolled! Check your dashboard to start the course."
                  : isEventRegistration
                  ? "You're registered! Check your dashboard for event details."
                  : 'Your subscription has been activated. You now have access to premium features.'}
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/dashboard">
                  <Button className="bg-[#003366] hover:bg-[#003366]/90" data-testid="go-to-dashboard">
                    Go to Dashboard
                  </Button>
                </Link>
                {isCourseEnrollment ? (
                  <Link to="/courses"><Button variant="outline">Browse Courses</Button></Link>
                ) : isEventRegistration ? (
                  <Link to="/events"><Button variant="outline">Browse Events</Button></Link>
                ) : (
                  <Link to="/jobs"><Button variant="outline">Browse Jobs</Button></Link>
                )}
              </div>
            </>
          )}
          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-red-700">
                {status === 'timeout' ? 'Payment Verification Timeout' : 'Payment Issue'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {status === 'timeout'
                  ? 'We could not verify your payment yet. It may still be processing.'
                  : 'Something went wrong. Please contact support if you were charged.'}
              </p>
              <Link to="/pricing">
                <Button variant="outline">Back to Pricing</Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
